// Family PIN System Types
// "Kids as data" - Family members (ages 5-12) stored as data records, not Cognito users
// Authenticated via shared family PIN

export type MemberType = 'child' | 'adult';
export type MemberAge = 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12; // Kids only

export interface FamilyMember {
  member_id: string;
  family_id: string;
  parent_user_id: string; // The Cognito user who created this member
  member_name: string; // First name only
  member_age: number; // 5-12 for kids
  member_type: MemberType;
  is_active: boolean;
  created_at: string;
  updated_at?: string;

  // Task assignment metadata
  assigned_task_count?: number;
  completed_task_count?: number;

  // Display preferences
  avatar_emoji?: string; // Kid-friendly emoji avatar
  favorite_color?: string; // For UI personalization
}

export interface FamilyPIN {
  family_id: string;
  pin_hash: string; // Server-side only
  created_at: string;
  last_regenerated?: string;
  expires_at?: string; // Optional PIN expiration
}

export interface FamilyPINValidationRequest {
  pin: string; // 4-digit PIN
}

export interface FamilyPINValidationResponse {
  valid: boolean;
  family_id?: string;
  members?: FamilyMember[];
  error_message?: string;
}

export interface FamilyMemberCreationRequest {
  member_name: string;
  member_age: number;
  member_type: MemberType;
}

export interface FamilyMemberUpdateRequest {
  member_name?: string;
  member_age?: number;
  is_active?: boolean;
  avatar_emoji?: string;
  favorite_color?: string;
}

export interface KidTaskAssignment {
  task_id: string;
  family_member_id: string; // Not user_id - assigned to family member data record
  assigned_by_user_id: string;
  assigned_at: string;
  completed_at?: string;
}

// API Response types
export interface FamilyMembersResponse {
  success: boolean;
  members: FamilyMember[];
  family_pin: string; // Only returned to parents
  total_count: number;
}

export interface FamilyMemberResponse {
  success: boolean;
  member: FamilyMember;
  message: string;
}

export interface PINRegenerateResponse {
  success: boolean;
  new_pin: string; // 4-digit PIN (shown once)
  message: string;
}

// Session storage for family member context
export interface FamilyMemberSession {
  member_id: string;
  member_name: string;
  member_age: number;
  family_id: string;
  logged_in_at: string;
}

// Validation helpers
export function isValidKidAge(age: number): age is MemberAge {
  return age >= 5 && age <= 12;
}

export function isValidPIN(pin: string): boolean {
  return /^\d{4}$/.test(pin);
}

export function isKidAppropriateTask(taskDifficulty: string, memberAge: number): boolean {
  // Simple tasks for younger kids, medium tasks for older kids
  if (memberAge >= 5 && memberAge <= 7) {
    return taskDifficulty === 'easy';
  } else if (memberAge >= 8 && memberAge <= 10) {
    return taskDifficulty === 'easy' || taskDifficulty === 'medium';
  } else {
    // Ages 11-12 can handle most tasks except hard
    return taskDifficulty !== 'hard';
  }
}

// Kid-friendly emoji avatars
export const KID_AVATARS = [
  '😊', '😎', '🤗', '😺', '🐶', '🐼', '🦁', '🐯',
  '🦊', '🐨', '🐸', '🦄', '🌈', '⭐', '🎨', '🎮',
  '⚽', '🏀', '🎸', '🎭', '🚀', '🌟', '💫', '✨'
];

// Kid-friendly color palette
export const KID_COLORS = [
  { name: 'Sunny Yellow', value: '#FCD34D' },
  { name: 'Ocean Blue', value: '#60A5FA' },
  { name: 'Grass Green', value: '#34D399' },
  { name: 'Bubblegum Pink', value: '#F472B6' },
  { name: 'Orange Crush', value: '#FB923C' },
  { name: 'Purple Magic', value: '#A78BFA' },
  { name: 'Cherry Red', value: '#F87171' },
  { name: 'Mint Fresh', value: '#6EE7B7' }
];
